import React from 'react'

import { cn } from '@/helpers/shadcn/utils'

import type { GeographicDistribution } from '@/types/usaspending'

import { Currency, NumberDisplay, Percentage } from '../Formatters'
import { STATE_CENTROIDS } from './data/us-state-centroids'

const STATIC_PREVIEW_MIN_HEIGHT = 760

function project(longitude: number, latitude: number) {
  const minLon = -162
  const maxLon = -66
  const minLat = 18
  const maxLat = 68
  return {
    x: ((longitude - minLon) / (maxLon - minLon)) * 100,
    y: ((maxLat - latitude) / (maxLat - minLat)) * 100,
  }
}

function SummaryCard({
  label,
  value,
  detail,
}: {
  label: string
  value: React.ReactNode
  detail: React.ReactNode
}) {
  return (
    <div className="rounded-xl border border-primary-200 bg-white/80 p-2.5 shadow-sm dark:border-primary-700 dark:bg-primary-900/70">
      <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-primary-500 dark:text-primary-400">
        {label}
      </div>
      <div className="mt-1 text-base font-semibold leading-none text-primary-900 dark:text-primary-100 sm:text-lg">
        {value}
      </div>
      <div className="mt-1 text-[11px] text-primary-500 dark:text-primary-400">
        {detail}
      </div>
    </div>
  )
}

export interface RecipientGeographyStaticMapProps {
  data: GeographicDistribution[]
  height?: number
  className?: string
}

export function RecipientGeographyStaticMap({
  data,
  height = 360,
  className,
}: RecipientGeographyStaticMapProps) {
  const mapStageHeight = Math.max(height, STATIC_PREVIEW_MIN_HEIGHT)
  const attributed = data.filter(
    (item): item is GeographicDistribution & { stateCode: string } =>
      Boolean(item.stateCode && STATE_CENTROIDS[item.stateCode]),
  )
  const unattributed = data.find((item) => !item.stateCode)
  const total = data.reduce((sum, item) => sum + item.amount, 0)
  const topStates = [...attributed]
    .sort((a, b) => b.amount - a.amount)
    .slice(0, 5)
  const maxAmount = Math.max(
    ...attributed.map((item) => Math.abs(item.amount)),
    1,
  )

  return (
    <div
      className={cn(
        'relative grid items-start gap-4 xl:grid-cols-[minmax(0,1fr)_18rem]',
        className,
      )}
    >
      <div className="flex min-w-0 flex-col gap-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="inline-flex rounded-lg border border-primary-200 bg-white p-1 shadow-sm dark:border-primary-700 dark:bg-primary-900">
            <div className="rounded-md bg-accent-100 px-3 py-1.5 text-xs font-semibold text-accent-700 dark:bg-accent-900 dark:text-accent-300">
              Cached Preview
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2 text-[11px] text-primary-500 dark:text-primary-400">
            <span className="rounded-full border border-primary-200 bg-white px-2.5 py-1 dark:border-primary-700 dark:bg-primary-900">
              {attributed.length} states mapped
            </span>
            <span className="rounded-full border border-primary-200 bg-white px-2.5 py-1 dark:border-primary-700 dark:bg-primary-900">
              <Percentage
                value={
                  total > 0 && unattributed
                    ? ((total - unattributed.amount) / total) * 100
                    : 100
                }
              />{' '}
              attributed
            </span>
          </div>
        </div>

        <div
          className="relative overflow-hidden rounded-xl border border-primary-200 bg-primary-50/60 shadow-sm dark:border-primary-700 dark:bg-primary-950/60"
          style={{ height: mapStageHeight, minHeight: mapStageHeight }}
        >
          <svg viewBox="0 0 1000 560" className="h-full w-full">
            <rect width="1000" height="560" fill="transparent" />
            <g
              stroke="currentColor"
              className="text-primary-200/60 dark:text-primary-800/70"
            >
              {Array.from({ length: 6 }).map((_, index) => (
                <line
                  key={`h-${index}`}
                  x1="0"
                  x2="1000"
                  y1={80 + index * 72}
                  y2={80 + index * 72}
                  strokeDasharray="4 4"
                />
              ))}
              {Array.from({ length: 8 }).map((_, index) => (
                <line
                  key={`v-${index}`}
                  y1="0"
                  y2="560"
                  x1={120 + index * 105}
                  x2={120 + index * 105}
                  strokeDasharray="4 4"
                />
              ))}
            </g>
            {attributed.map((state) => {
              const centroid = STATE_CENTROIDS[state.stateCode]
              const point = project(centroid.longitude, centroid.latitude)
              const radius =
                8 + Math.sqrt(Math.abs(state.amount) / maxAmount) * 18
              const isLabeled = topStates.some(
                (candidate) => candidate.stateCode === state.stateCode,
              )
              return (
                <g
                  key={state.stateCode}
                  transform={`translate(${point.x * 10}, ${point.y * 5.6})`}
                >
                  <circle
                    r={radius + 5}
                    fill="rgba(99, 102, 241, 0.18)"
                    stroke="rgba(99, 102, 241, 0.18)"
                  />
                  <circle
                    r={radius}
                    fill="rgba(96, 165, 250, 0.9)"
                    stroke="rgba(255,255,255,0.7)"
                    strokeWidth="2"
                  />
                  {isLabeled && (
                    <text
                      y={radius >= 18 ? 4 : -radius - 10}
                      textAnchor="middle"
                      className="fill-primary-900 text-[9px] font-semibold uppercase dark:fill-primary-100"
                    >
                      {state.stateCode}
                    </text>
                  )}
                </g>
              )
            })}
          </svg>

          <div className="pointer-events-none absolute inset-x-0 bottom-0 flex flex-wrap items-end justify-between gap-2 bg-gradient-to-t from-white/95 via-white/65 to-transparent px-3 pb-3 pt-8 dark:from-primary-950/95 dark:via-primary-950/65">
            <div className="rounded-full border border-primary-200 bg-white/90 px-3 py-1.5 text-[11px] font-medium text-primary-600 shadow-sm backdrop-blur dark:border-primary-700 dark:bg-primary-900/90 dark:text-primary-300">
              Cached geography preview while the interactive map hydrates.
            </div>
          </div>
        </div>
      </div>

      <aside className="space-y-3">
        <div className="grid gap-2 sm:grid-cols-3 xl:grid-cols-1">
          <SummaryCard
            label="Total"
            value={<Currency value={total} compact />}
            detail="Obligated amount"
          />
          <SummaryCard
            label="Coverage"
            value={
              <Percentage
                value={
                  total > 0 && unattributed
                    ? ((total - unattributed.amount) / total) * 100
                    : 100
                }
              />
            }
            detail="Attributed to a mapped state"
          />
          <SummaryCard
            label="Largest footprint"
            value={topStates[0]?.stateCode ?? '—'}
            detail={
              topStates[0] ? (
                <>
                  {topStates[0].stateName} •{' '}
                  <Percentage value={(topStates[0].amount / total) * 100} />
                </>
              ) : (
                'No mapped state data'
              )
            }
          />
        </div>

        <div className="rounded-xl border border-primary-200 bg-white shadow-sm dark:border-primary-700 dark:bg-primary-900">
          <div className="border-b border-primary-100 px-3.5 py-2.5 dark:border-primary-800">
            <div className="text-xs font-bold uppercase tracking-wider text-primary-900 dark:text-primary-100">
              Top states
            </div>
            <div className="mt-0.5 text-xs text-primary-500 dark:text-primary-400">
              Cached summary before interactive hydration.
            </div>
          </div>

          <div className="divide-y divide-primary-100 dark:divide-primary-800">
            {topStates.map((state) => (
              <div
                key={state.stateCode}
                className="flex items-start gap-2.5 px-3.5 py-2.5"
              >
                <div className="flex h-9 min-w-9 shrink-0 items-center justify-center rounded-md border border-primary-200 bg-primary-50 px-2 text-[11px] font-bold uppercase text-primary-700 shadow-sm dark:border-primary-700 dark:bg-primary-900 dark:text-primary-200">
                  {state.stateCode}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-semibold text-primary-900 dark:text-primary-100">
                    {state.stateName}
                  </div>
                  <div className="mt-0.5 flex flex-wrap items-center gap-1.5 text-xs text-primary-500 dark:text-primary-400">
                    <span>
                      <Currency value={state.amount} compact />
                    </span>
                    <span>•</span>
                    <span>
                      <Percentage value={(state.amount / total) * 100} />
                    </span>
                  </div>
                  <div className="mt-0.5 text-[11px] text-primary-400 dark:text-primary-500">
                    <NumberDisplay value={state.awardCount} compact /> awards
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </aside>
    </div>
  )
}
